"use client";

import React, { useEffect, useRef, useState } from "react";
import type { IListing } from "@/src/shared/types/listing";
import InitialAvatar from "@/src/shared/components/atoms/InitialAvatar";
import { cn } from "@/src/lib/utils";
import { useListingContactQuery } from "../api/queries";
import { listingService } from "../services";

interface IContactHostIntroProps {
  listing: IListing;
  /** "12–15 Oct" when the guest has picked dates; the "available … Book" line needs them. */
  datesLabel: string | null;
  onMessageHost: () => void;
  onBook: () => void;
}

/**
 * The app's Contact host screen (`ContactHostPage`): who the host is and how fast they
 * answer, then the listing's questions answered in accordions (Python `/contact`), and
 * "Message host" for anything else.
 */
const ContactHostIntro: React.FC<IContactHostIntroProps> = ({
  listing,
  datesLabel,
  onMessageHost,
  onBook,
}) => {
  const contactQuery = useListingContactQuery(listing.id, true);
  const contact = contactQuery.data;
  const hostName = listing.host?.first_name ?? "";
  const sections = listingService.contactSections(contact);
  const photo = contact?.host.image?.url || listing.host?.image?.url;
  const [expanded, setExpanded] = useState<string[]>([]);
  // Once the sections arrive: all open on desktop, the first one on a phone.
  const opened = useRef(false);
  useEffect(() => {
    const first = sections[0];
    if (opened.current || !first) return;
    opened.current = true;
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    setExpanded(
      isDesktop ? sections.map((section) => section.key) : [first.key],
    );
  }, [sections]);

  const toggle = (key: string) =>
    setExpanded((keys) =>
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key],
    );

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6">
        <div className="flex items-start gap-4 pb-6 pt-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-22 font-semibold leading-7 text-ink">
              {listingService.contactTitle(contact, listing.host ?? null)}
            </h2>
            {contact?.subtitle && (
              <p className="mt-1 text-14 leading-5 text-ink-secondary">
                {contact.subtitle}
              </p>
            )}
          </div>
          {photo ? (
            <img
              src={photo}
              alt={hostName}
              className="h-14 w-14 shrink-0 rounded-full object-cover"
            />
          ) : (
            <InitialAvatar title={hostName} width="w-14" height="h-14" />
          )}
        </div>

        {sections.length > 0 ? (
          sections.map((section) => {
            const isOpen = expanded.includes(section.key);
            return (
              <div key={section.key}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggle(section.key)}
                  className="flex w-full items-center gap-3 py-4 text-left"
                >
                  <span className="flex-1 text-16 font-medium leading-6 text-ink">
                    {section.title}
                  </span>
                  <img
                    src="/icons/listing/listing_chevron_down.svg"
                    alt=""
                    className={cn(
                      "h-6 w-6 shrink-0 transition-transform duration-200",
                      isOpen && "rotate-180",
                    )}
                  />
                </button>
                {isOpen && (
                  <ul className="space-y-3 pb-4 pl-9">
                    {section.items.map((item) => (
                      <li
                        key={item}
                        className="flex text-14 leading-5 text-ink"
                      >
                        <span className="w-3 shrink-0">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        ) : contactQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-ink-faint border-t-ink" />
          </div>
        ) : contactQuery.isError ? (
          <div className="py-4">
            <p className="text-14 leading-5 text-ink-secondary">
              Could not load the host&apos;s details.
            </p>
            <button
              type="button"
              onClick={() => void contactQuery.refetch()}
              className="mt-3 rounded-lg bg-[#F7F7F7] px-4 py-2.5 text-14 font-semibold text-ink"
            >
              Try again
            </button>
          </div>
        ) : null}

        {/* No line under the last section. */}
        <div className="mb-6 mt-4" />
        <div className="flex-1" />

        {datesLabel && (
          <p className="px-4 pb-5 text-center text-16 leading-6 text-ink">
            This home is available {datesLabel}.{" "}
            <button
              type="button"
              onClick={onBook}
              className="font-medium text-brand-dark underline"
            >
              Book
            </button>
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[#EBEBEB] px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <p className="text-14 leading-5 text-ink-secondary">
          Still have a question?
        </p>
        <button
          type="button"
          onClick={onMessageHost}
          className="h-14 shrink-0 rounded-xl bg-ink px-6 text-16 font-semibold leading-5 text-white active:opacity-80"
        >
          Message host
        </button>
      </div>
    </>
  );
};

export default ContactHostIntro;
